import { RefObject } from 'react'
import { comparer, reaction, runInAction } from 'mobx'
import { SnackbarProvider } from 'notistack'
import { AxiosRequestConfig } from 'axios'
import debounce from 'lodash/debounce'
import JSZip from 'jszip'
import { getUnixTime } from 'date-fns'

import { ApiLogic } from './apiLogic'
import { HandlersContainer } from './handlersContainer'

import { Store } from '../store'

import { showUiError } from '../../hooks/snackbarHooks'

import { required } from '../../common/objectUtils'
import { appAuthRoutes } from '../../common/appRoutes'
import { isLocalStorageAllowed } from '../../common/commonUtils'
import { UnprocessedValueError } from '../../common/tsUtils'

import { StoreAccountModel } from '../../types/accountProps'
import { MyProductColumnId } from '../../types/myProductColumnIds'
import { StoreIntegrationType } from '../../types/integrationTypeUtils'

import { PrintResult } from '../../server/mpsklad_core/Models/PrintResult'
import { MoySkladAppType } from '../../server/mpsklad_common/MoySkladAppType'
import { MsProductProp } from '../../server/mpsklad_core/Models/MsProductProp'
import { WbProductProp } from '../../server/mpsklad_core/Models/WbProductProp'
import { YmProductProp } from '../../server/mpsklad_core/Models/YmProductProp'
import { UserAuthModel } from '../../server/mpsklad_core/Models/UserAuthModel'
import { MyProductModel } from '../../server/mpsklad_core/Models/MyProductModel'
import { WbAccountModel } from '../../server/mpsklad_core/Models/WbAccountModel'
import { WbProductModel } from '../../server/mpsklad_core/Models/WbProductModel'
import { IntegrationType } from '../../server/mpsklad_core/Entity/IntegrationType'
import { OzonProductProp } from '../../server/mpsklad_core/Models/OzonProductProp'
import { PaymentSource } from '../../server/mpsklad_core/Entity/Enums/PaymentSource'
import { MoySkladAppState } from '../../server/mpsklad_core/Models/MoySkladAppState'
import { OzonAccountModel } from '../../server/mpsklad_core/Models/OzonAccountModel'
import { OzonProductModel } from '../../server/mpsklad_core/Models/OzonProductModel'
import { TablePageOptions } from '../../server/mpsklad_core/Models/TablePageOptions'
import { StoreProductModel } from '../../server/mpsklad_core/Models/StoreProductModel'
import { CommisionsSettings } from '../../server/mpsklad_core/Models/CommisionsSettings'
import { EditWbAccountModel } from '../../server/mpsklad_core/Models/EditWbAccountModel'
import { RegisterModel } from '../../server/mpsklad_core/Models/ApiModels/RegisterModel'
import { SubscriptionType } from '../../server/mpsklad_core/Entity/Enums/SubscriptionType'
import { EditOzonAccountModel } from '../../server/mpsklad_core/Models/EditOzonAccountModel'
import { MoySkladStatusMapModel } from '../../server/mpsklad_core/Models/MoySkladStatusMapModel'
import { EditMoySkladAccountModel } from '../../server/mpsklad_core/Models/EditMoySkladAccountModel'
import { YandexMarketAccountModel } from '../../server/mpsklad_core/Models/YandexMarketAccountModel'
import { YandexMarketProductModel } from '../../server/mpsklad_core/Models/YandexMarketProductModel'
import { SetStoreProductsSyncModel } from '../../server/mpsklad_core/Models/SetStoreProductsSyncModel'
import { MoySkladSetAttributesModel } from '../../server/mpsklad_core/Models/MoySkladSetAttributesModel'
import { MyProductsTableOrderColumn } from '../../server/mpsklad_core/Models/MyProductsTableOrderColumn'
import { MyProductsTableFilterColumn } from '../../server/mpsklad_core/Models/MyProductsTableFilterColumn'
import { EditYandexMarketAccountModel } from '../../server/mpsklad_core/Models/EditYandexMarketAccountModel'
import { SetAutoMatchNewStoreProductsModel } from '../../server/mpsklad_core/Models/SetAutoMatchNewStoreProductsModel'

export class Logic {
  private readonly hiddenMyProductColumnIdsKey = 'hiddenMyProductColumnIds'

  private readonly store: Store

  private readonly showUiError: (uiMessage: string) => void

  readonly api: ApiLogic

  readonly ozonOrderLoaders = new HandlersContainer()

  readonly wbOrderLoaders = new HandlersContainer()

  readonly yandexMarketOrderLoaders = new HandlersContainer()

  readonly msOrderLoaders = new HandlersContainer()

  constructor(store: Store, snackbarRef: RefObject<SnackbarProvider>) {
    this.showUiError = (uiMessage: string) => showUiError(snackbarRef, uiMessage)

    this.api = new ApiLogic(this.showUiError)

    this.store = store

    this.store.productStore.init(
      this.myProductsPageLoader,
      this.api.product.getOzonPage, this.api.product.getWbPage, this.api.product.getYandexMarketPage,
      this.api.product.getMoySkladPage)

    this.store.orderStore.init(this.api.order.getOzonPage, this.api.order.getWbPage, this.api.order.getYandexMarketPage,
      this.api.order.getMoySkladPage)

    this.store.adminStore.users.init(this.api.admin.users)

    this.loadMyProductColumnIds()
    this.trackMyProductColumnIds()

    this.trackAuthToken()
  }

  healthCheck =
    (): Promise<boolean> =>
      this.api.healthCheck().catch(() => false)

  loadUserAccounts =
    async () => {
      const {ozonAccounts, wbAccounts, yandexMarketAccounts, moySkladAccount} = await this.api.userSync.getSync()

      runInAction(() => {
        this.store.syncStore.ozonAccounts = ozonAccounts
        this.store.syncStore.wbAccounts = wbAccounts
        this.store.syncStore.yandexMarketAccounts = yandexMarketAccounts
        this.store.syncStore.moySkladAccount = moySkladAccount ?? null
      })
    }

  setOzonAccountSync =
    async (syncEnabled: boolean, ozonAccountId: number) => {
      await this.api.userSync.setOzonSync({isEnabled: syncEnabled, accountId: ozonAccountId})
      await this.loadUserAccounts()
    }

  setOzonMultiProducts =
    async (allow: boolean) => {
      await this.api.userSync.setOzonMultiProducts(allow)

      const user = required(this.store.user)
      user.allowMultiOzonProducts = allow
    }

  setWbMultiProducts =
    async (allow: boolean) => {
      await this.api.userSync.setWbMultiProducts(allow)

      const user = required(this.store.user)
      user.allowMultiWbProducts = allow
    }

  setWbAccountSync =
    async (syncEnabled: boolean, wbAccountId: number) => {
      await this.api.userSync.setWbSync({isEnabled: syncEnabled, accountId: wbAccountId})
      await this.loadUserAccounts()
    }

  setYandexMarketAccountSync =
    async (syncEnabled: boolean, yandexMarketAccountId: number) => {
      await this.api.userSync.setYandexMarketSync({isEnabled: syncEnabled, accountId: yandexMarketAccountId})
      await this.loadUserAccounts()
    }

  setYandexMarketMultiProducts =
    async (allow: boolean) => {
      await this.api.userSync.setYandexMarketMultiProducts(allow)

      const user = required(this.store.user)
      user.allowMultiYandexMarketProducts = allow
    }

  setMoySkladAccountSync =
    async (syncEnabled: boolean) => {
      await this.api.userSync.setMoySkladSync(syncEnabled)
      await this.loadUserAccounts()
    }

  setMsOrderAttributes =
    async (model: MoySkladSetAttributesModel) => {
      await this.api.userSync.setMoySkladAttributes(model)
      await this.loadUserAccounts()
    }

  setOzonProductSync =
    async (msProductId: number, ozonProduct: OzonProductModel, syncEnabled: boolean) => {
      const product = await this.api.product.setOzonSync({
        msProductId,
        syncEnabled,
        storeProductId: ozonProduct.id,
        accountId: ozonProduct.accountId,
        integrationType: IntegrationType.Ozon
      })

      this.store.productStore.replaceProduct(product)
      setTimeout(this.loadMyProductsInfo)
    }

  setWbProductSync =
    async (msProductId: number, wbProduct: WbProductModel, syncEnabled: boolean) => {
      const product = await this.api.product.setWbSync({
        msProductId,
        syncEnabled,
        storeProductId: wbProduct.id,
        accountId: wbProduct.accountId,
        integrationType: IntegrationType.Wildberries
      })

      this.store.productStore.replaceProduct(product)
      setTimeout(this.loadMyProductsInfo)
    }

  setYandexMarketProductSync =
    async (msProductId: number, yandexMarketProduct: YandexMarketProductModel, syncEnabled: boolean) => {
      const product = await this.api.product.setYandexMarketSync({
        msProductId,
        syncEnabled,
        storeProductId: yandexMarketProduct.id,
        accountId: yandexMarketProduct.accountId,
        integrationType: IntegrationType.YandexMarket
      })

      this.store.productStore.replaceProduct(product)
      setTimeout(this.loadMyProductsInfo)
    }

  private get storeProductsState() {
    return required(this.store.productStore.storeMountState)
  }

  private loadMyProductsInfo =
    async () => {
      if (this.storeProductsState.accountId <= 0) {
        throw new Error('Bad account id')
      }

      this.store.productStore.myProductsInfo =
        await this.api.product.getMyInfo({
          integrationType: this.storeProductsState.integration,
          accountId: this.storeProductsState.accountId
        })
    }

  private loadMyProductsInfoDebounced =
    // debounce is practically required - some React effects are fired out of order with mobx reactions
    debounce(this.loadMyProductsInfo, 50)

  reloadMyProductsInfo =
    async () => {
      this.store.productStore.myProductsInfo = null
      await this.loadMyProductsInfoDebounced()
    }

  private myProductsPageLoader =
    (options: TablePageOptions<typeof MyProductsTableFilterColumn, typeof MyProductsTableOrderColumn>,
     config: AxiosRequestConfig | undefined) =>
      this.api.product.getMyPage({
        ...options,
        integrationType: this.storeProductsState.integration,
        accountId: this.storeProductsState.accountId,
        productsStateTab: this.store.productStore.myStateTab
      }, config)

  refreshMyProductsTab =
    () => this.store.productStore.myProducts.load()

  loadProductDiffs =
    async () => {
      if (this.store.syncStore.moySkladAccount != null) {
        this.store.productStore.productDiffs = await this.api.product.getDiffs()
      }
    }

  loadOzonProductOptions =
    (msProductId: number | undefined, searchTerm: string | undefined) =>
      this.storeProductsState.integration === IntegrationType.Ozon
      ? this.api.product.getOzonProductOptions({
        externalAccountId: this.storeProductsState.accountId,
        msProductId,
        searchTerm
      })
      : Promise.resolve([])

  loadWbProductOptions =
    (msProductId: number | undefined, searchTerm: string | undefined) =>
      this.storeProductsState.integration === IntegrationType.Wildberries
      ? this.api.product.getWbProductOptions({
        externalAccountId: this.storeProductsState.accountId,
        msProductId,
        searchTerm
      })
      : Promise.resolve([])

  loadYandexMarketProductOptions =
    (msProductId: number | undefined, searchTerm: string | undefined) =>
      this.storeProductsState.integration === IntegrationType.YandexMarket
      ? this.api.product.getYandexMarketProductOptions({
        externalAccountId: this.storeProductsState.accountId,
        msProductId,
        searchTerm
      })
      : Promise.resolve([])

  checkAuth =
    async (): Promise<UserAuthModel | null> => {
      // Split the variable for proper IDE analysis
      let user: UserAuthModel

      try {
        user = await this.api.auth.check()
      } catch (e) {
        console.error('Auth check failed', e)
        this.store.user = null
        return this.store.user
      }

      if (user.isImpersonating && user.moySkladAppState != null && user.moySkladAppState !== MoySkladAppState.Active) {
        this.showUiError(`Неактивное приложение МоегоСклада: ${MoySkladAppState[user.moySkladAppState]}`)
        user.moySkladAppState = MoySkladAppState.Active
      }

      this.store.user = user
      await this.loadUserAccounts()
      return this.store.user
    }

  private checkMoySkladAppState =
    async () => {
      if (this.store.user?.moySkladAppState === MoySkladAppState.SettingsRequired) {
        // TODO: null vs undefined in API models
        this.store.user.moySkladAppState = await this.api.auth.checkMoySkladAppState() ?? undefined
      }
    }

  private ensureNoAuth = () => {
    if (this.store.hasAuth) {
      throw new Error('Already has auth!')
    }
  }

  login =
    async (email: string, password: string) => {
      this.ensureNoAuth()

      await this.api.auth.login({email, password})
      await this.checkAuth()
    }

  loginMoySkladApp =
    async (appType: MoySkladAppType, contextKey: string) => {
      this.ensureNoAuth()

      this.store.authToken = await this.api.auth.loginMoySkladApp({appType, contextKey})
      await this.checkAuth()
    }

  register =
    async (model: RegisterModel) => {
      this.ensureNoAuth()

      await this.api.auth.register(model)
      await this.checkAuth()
    }

  logout =
    async () => {
      if (!await this.showDialog('Выйти из аккаунта?', {acceptButton: 'Выйти'})) {
        return
      }

      await this.api.auth.logOut()
      this.store.authToken = null

      // Reload is simpler than resetting the store
      window.location.assign(appAuthRoutes.Login)
    }

  sendPasswordResetCodeEmail =
    async (email: string) => {
      this.ensureNoAuth()

      await this.api.auth.requestPasswordReset({email})

      this.store.passwordResetStore.setEmail(email)
    }

  verifyResetCode =
    async (resetCode: string) => {
      this.ensureNoAuth()

      const {passwordResetStore} = this.store

      await this.api.auth.verifyResetCode({
        email: required(passwordResetStore.email),
        resetCode
      })

      passwordResetStore.setResetCode(resetCode)
    }

  resetPassword =
    async (newPassword: string) => {
      this.ensureNoAuth()

      const {passwordResetStore} = this.store

      await this.api.auth.resetPassword({
        email: required(passwordResetStore.email),
        resetCode: required(passwordResetStore.resetCode),
        newPassword
      })

      passwordResetStore.setFinished()
      await this.checkAuth()
    }

  setOzonProducts =
    async (msProductId: number, ozonProductIds: number[]) => {
      const product = this.store.productStore.findProduct(msProductId)

      product.ozonProducts =
        await this.api.product.setOzonProducts({
          msProductId,
          externalProductIds: ozonProductIds,
          externalAccountId: this.storeProductsState.accountId
        })

      setTimeout(this.loadMyProductsInfo)
    }

  setWbProducts =
    async (msProductId: number, wbProductIds: number[]) => {
      const product = this.store.productStore.findProduct(msProductId)

      product.wbProducts =
        await this.api.product.setWbProducts({
          msProductId,
          externalProductIds: wbProductIds,
          externalAccountId: this.storeProductsState.accountId
        })

      setTimeout(this.loadMyProductsInfo)
    }

  setYandexMarketProducts =
    async (msProductId: number, yandexMarketProductIds: number[]) => {
      const product = this.store.productStore.findProduct(msProductId)

      product.yandexMarketProducts =
        await this.api.product.setYandexMarketProducts({
          msProductId,
          externalProductIds: yandexMarketProductIds,
          externalAccountId: this.storeProductsState.accountId
        })

      setTimeout(this.loadMyProductsInfo)
    }

  ozonSyncSelectedProducts =
    (syncEnabled: boolean) =>
      this.syncSelectedProducts(syncEnabled, IntegrationType.Ozon,
        _ => _.ozonProducts, this.api.product.setOzonSyncMultiple)

  wbSyncSelectedProducts =
    (syncEnabled: boolean) =>
      this.syncSelectedProducts(syncEnabled, IntegrationType.Wildberries,
        _ => _.wbProducts, this.api.product.setWbSyncMultiple)

  yandexMarketSyncSelectedProducts =
    (syncEnabled: boolean) =>
      this.syncSelectedProducts(syncEnabled, IntegrationType.YandexMarket,
        _ => _.yandexMarketProducts,
        this.api.product.setYandexMarketSyncMultiple)

  private syncSelectedProducts =
    async (syncEnabled: boolean,
           storeAccountType: IntegrationType,
           mapStoreProducts: (myProduct: MyProductModel) => StoreProductModel[],
           invokeApi: (model: SetStoreProductsSyncModel) => Promise<void>): Promise<void> => {
      const {productStore} = this.store

      if (this.storeProductsState.integration !== storeAccountType) {
        throw new Error(`Bad store type: ${this.storeProductsState.integration} vs ${storeAccountType}`)
      }

      await invokeApi({
        syncEnabled,
        accountId: this.storeProductsState.accountId,
        productIds: productStore.myProducts.selectedRows.flatMap(mapStoreProducts).map(_ => _.id)
      })

      setTimeout(this.loadMyProductsInfo)
      setTimeout(this.refreshMyProductsTab)
    }

  createOzonAccount =
    async (model: EditOzonAccountModel): Promise<OzonAccountModel> => {
      const createdAccount = await this.api.userSync.createOzonAccount(model)
      this.store.syncStore.ozonAccounts.push(createdAccount)

      return createdAccount
    }

  editOzonAccount =
    async (model: EditOzonAccountModel) => {
      const editedAccount = await this.api.userSync.editOzonAccount(model)
      this.store.syncStore.replaceStoreAccount(editedAccount)
    }

  deleteOzonAccount =
    async (accountId: number) => {
      await this.api.userSync.deleteOzonAccount(accountId)
      await this.loadUserAccounts()
    }

  createWbAccount =
    async (model: EditWbAccountModel): Promise<WbAccountModel> => {
      const createdAccount = await this.api.userSync.createWbAccount(model)
      this.store.syncStore.wbAccounts.push(createdAccount)

      return createdAccount
    }

  editWbAccount =
    async (model: EditWbAccountModel) => {
      const editedAccount = await this.api.userSync.editWbAccount(model)
      this.store.syncStore.replaceStoreAccount(editedAccount)
    }

  deleteWbAccount =
    async (accountId: number) => {
      await this.api.userSync.deleteWbAccount(accountId)
      await this.loadUserAccounts()
    }

  createYandexMarketAccount =
    async (model: EditYandexMarketAccountModel): Promise<YandexMarketAccountModel> => {
      const createdAccount = await this.api.userSync.createYandexMarketAccount(model)
      this.store.syncStore.yandexMarketAccounts.push(createdAccount)

      return createdAccount
    }

  editYandexMarketAccount =
    async (model: EditYandexMarketAccountModel) => {
      const editedAccount = await this.api.userSync.editYandexMarketAccount(model)
      this.store.syncStore.replaceStoreAccount(editedAccount)
    }

  deleteYandexMarketAccount =
    async (accountId: number) => {
      await this.api.userSync.deleteYandexMarketAccount(accountId)
      await this.loadUserAccounts()
    }

  deleteAccount =
    async (account: StoreAccountModel) => {
      const storeType = account.integrationType

      switch (storeType) {
        case IntegrationType.Ozon:
          await this.deleteOzonAccount(account.id)
          break

        case IntegrationType.Wildberries:
          await this.deleteWbAccount(account.id)
          break

        case IntegrationType.YandexMarket:
          await this.deleteYandexMarketAccount(account.id)
          break

        default:
          throw new UnprocessedValueError('IntegrationType', storeType)
      }
    }

  editMoySkladAccount =
    async (model: EditMoySkladAccountModel) => {
      this.store.syncStore.moySkladAccount = await this.api.userSync.editMoySkladAccount(model)
    }

  // TODO: Add a UI Logic class?
  confirmSyncEnable =
    () => this.showDialog('Внимание, после включения синхронизации остатки на маркетплейсах будут перезаписаны!')

  confirmSyncDisable =
    () => this.showDialog('Синхронизация будет отключена. Заказы и остатки перестанут обновляться.')

  showDialog =
    (text: string,
     options?: {
       acceptButton?: string
       declineButton?: string
       title?: string
     }): Promise<boolean> => {
      if (this.store.dialog !== null) {
        throw new Error('Another dialog is already shown')
      }

      return new Promise(resolve =>
        this.store.dialog = {
          text,
          title: options?.title ?? 'Продолжить?',
          acceptButton: options?.acceptButton ?? 'Продолжить',
          declineButton: options?.declineButton ?? 'Отмена',
          onClose: resolve
        })
    }

  acceptDialog = () => {
    required(this.store.dialog).onClose(true)
    this.store.dialog = null
  }

  declineDialog = () => {
    required(this.store.dialog).onClose(false)
    this.store.dialog = null
  }

  toggleMyProductsColumn =
    (columnId: MyProductColumnId) => {
      if (this.store.hiddenMyProductColumnIds.has(columnId)) {
        this.store.hiddenMyProductColumnIds.delete(columnId)
      } else {
        this.store.hiddenMyProductColumnIds.add(columnId)
      }
    }

  matchOzonToMoySkladProducts =
    async (ozonProp: OzonProductProp, moySkladProp: MsProductProp, accountIdFrom: number): Promise<number> =>
      await this.matchProducts(
        () => this.api.userSync.matchOzonToMoySkladProducts({fromProp: ozonProp, toProp: moySkladProp, accountIdFrom}))

  matchWbToMoySkladProducts =
    async (wbProp: WbProductProp, moySkladProp: MsProductProp, accountIdFrom: number): Promise<number> =>
      await this.matchProducts(
        () => this.api.userSync.matchWbToMoySkladProducts({fromProp: wbProp, toProp: moySkladProp, accountIdFrom}))

  matchYandexMarketToMoySkladProducts =
    async (ymProp: YmProductProp, moySkladProp: MsProductProp, accountIdFrom: number): Promise<number> =>
      await this.matchProducts(
        () => this.api.userSync.matchYandexMarketToMoySkladProducts(
          {fromProp: ymProp, toProp: moySkladProp, accountIdFrom}))

  private matchProducts =
    async (invokeApi: () => Promise<number>): Promise<number> => {
      const productCount = await invokeApi()

      setTimeout(this.loadMyProductsInfo)
      setTimeout(this.refreshMyProductsTab)

      return productCount
    }

  unmatchOzonProducts =
    async (ozonAccountId: number) => {
      await this.api.userSync.unmatchOzonProducts(ozonAccountId)

      await this.loadMyProductsInfo()
      await this.refreshMyProductsTab()
    }

  unmatchWbProducts =
    async (wbAccountId: number) => {
      await this.api.userSync.unmatchWbProducts(wbAccountId)

      await this.loadMyProductsInfo()
      await this.refreshMyProductsTab()
    }

  unmatchYandexMarketProducts =
    async (yandexMarketAccountId: number) => {
      await this.api.userSync.unmatchYandexMarketProducts(yandexMarketAccountId)

      await this.loadMyProductsInfo()
      await this.refreshMyProductsTab()
    }

  setAutoMatchNewOzonProducts =
    async (model: SetAutoMatchNewStoreProductsModel, ozonProp: OzonProductProp) => {
      const account = this.store.syncStore.getOzonAccount(model.accountId)

      await this.api.userSync.setAutoMatchNewOzonProducts({...model, ozonProp})

      runInAction(() => {
        account.autoMatchNewProducts = model.isEnabled
        account.autoMatchMsProp = model.msProp
        account.autoMatchOzonProp = ozonProp
      })
    }

  setAutoMatchNewWbProducts =
    async (model: SetAutoMatchNewStoreProductsModel, wbProp: WbProductProp) => {
      const account = this.store.syncStore.getWbAccount(model.accountId)

      await this.api.userSync.setAutoMatchNewWbProducts({...model, wbProp})

      runInAction(() => {
        account.autoMatchNewProducts = model.isEnabled
        account.autoMatchMsProp = model.msProp
        account.autoMatchWbProp = wbProp
      })
    }

  setAutoMatchNewYmProducts =
    async (model: SetAutoMatchNewStoreProductsModel, ymProp: YmProductProp) => {
      const account = this.store.syncStore.getYandexMarketAccount(model.accountId)

      await this.api.userSync.setAutoMatchNewYmProducts({...model, ymProp})

      runInAction(() => {
        account.autoMatchNewProducts = model.isEnabled
        account.autoMatchMsProp = model.msProp
        account.autoMatchYmProp = ymProp
      })
    }

  setMoySkladStatusMaps =
    async (model: MoySkladStatusMapModel[]) => {
      await this.api.userSync.setMoySkladStatusMaps(model)
      setTimeout(this.checkMoySkladAppState)
    }

  setUserSync =
    async (userId: string, allowSync: boolean) => {
      await this.api.admin.setUserSync({userId, allowSync})
      this.findAdminUser(userId).allowSync = allowSync
    }

  setFakeApiFlag =
    async (userId: string, useFakeApi: boolean) => {
      await this.api.admin.setFakeApiFlag({userId, useFakeApi})
      this.findAdminUser(userId).isUsingFakeApi = useFakeApi
    }

  setUserSubscription =
    async (userId: string, subscriptionType: SubscriptionType) => {
      // TODO: Use response model for all user updates - this relies on backend implementation of ComputeIsAtRisk
      const newUserModel = await this.api.admin.setUserSubscriptionType({userId, subscriptionType})
      this.store.adminStore.users.replaceRow(newUserModel, _ => _.id)
    }

  setUserLastPaymentDate =
    async (userId: string, lastPaymentDate: Date | null) => {
      const lastPaymentDateUnix = lastPaymentDate ? getUnixTime(lastPaymentDate) : undefined
      await this.api.admin.setUserLastPaymentDate({userId, lastPaymentDate: lastPaymentDateUnix})
      this.findAdminUser(userId).lastPaymentDate = lastPaymentDateUnix
    }

  setUserPaymentSource =
    async (userId: string, paymentSource: PaymentSource) => {
      await this.api.admin.setUserPaymentSource({userId, paymentSource})
      this.findAdminUser(userId).paymentSource = paymentSource
    }

  setUserSubscriptionEndDate =
    async (userId: string, endDate: Date | null) => {
      const newUserModel =
        await this.api.admin.setUserSubscriptionEndDate({
          userId,
          endDate: endDate ? getUnixTime(endDate) : undefined
        })

      this.store.adminStore.users.replaceRow(newUserModel, _ => _.id)
    }

  setUserAdminComment =
    async (userId: string, comment: string | null | undefined) => {
      comment = comment || undefined
      await this.api.admin.setUserAdminComment({userId, comment})
      this.findAdminUser(userId).adminComment = comment
    }

  private findAdminUser =
    (userId: string) =>
      required(this.store.adminStore.users.data.find(_ => _.id === userId))

  private loadMyProductColumnIds =
    () => {
      if (!isLocalStorageAllowed) {
        return
      }

      try {
        const hiddenColumnIdsJson = localStorage.getItem(this.hiddenMyProductColumnIdsKey)

        if (!hiddenColumnIdsJson) {
          return
        }

        const hiddenColumnIds = new Set<MyProductColumnId>(JSON.parse(hiddenColumnIdsJson))

        if (hiddenColumnIds.size > 0) {
          this.store.hiddenMyProductColumnIds = hiddenColumnIds
        }
      } catch (e) {
        console.error('Failed to load hiddenMyProductColumnIds', e)

        try {
          localStorage.removeItem(this.hiddenMyProductColumnIdsKey)
        } catch (e2) {
          console.error('Failed to clear hiddenMyProductColumnIds', e2)
        }
      }
    }

  private trackMyProductColumnIds =
    () => {
      if (!isLocalStorageAllowed) {
        return
      }

      return reaction(
        () => Array.from(this.store.hiddenMyProductColumnIds),
        hiddenColumnIds => {
          try {
            localStorage.setItem(this.hiddenMyProductColumnIdsKey, JSON.stringify(hiddenColumnIds))
          } catch (e) {
            console.error('Failed to save hiddenMyProductColumnIds', e)
          }
        },
        {equals: comparer.shallow})
    }

  private trackAuthToken =
    () => reaction(
      () => this.store.authToken,
      authToken => this.api.headerAuthToken = authToken)

  pullOzonOrder =
    async (ozonAccountId: number, ozonOrderId: number) => {
      await this.api.order.pullOzon(ozonOrderId)
      await this.ozonOrderLoaders.trigger()
    }

  pullWbOrder =
    async (wbAccountId: number, wbOrderId: number) => {
      await this.api.order.pullWb(wbOrderId)
      await this.wbOrderLoaders.trigger()
    }

  pullYandexMarketOrder =
    async (yandexMarketAccountId: number, yandexMarketOrderId: number) => {
      await this.api.order.pullYandexMarket(yandexMarketOrderId)
      await this.yandexMarketOrderLoaders.trigger()
    }

  pullMoySkladOrder =
    async (msOrderId: number) => {
      await this.api.order.pullMoySklad(msOrderId)
      await this.msOrderLoaders.trigger()
    }

  loadOzonFiles =
    async (ozonAccountId: number): Promise<void> => {
      this.store.labelStore.ozonFiles.data = await this.api.label.ozonFiles(ozonAccountId)
    }

  loadWbFiles =
    async (wbAccountId: number): Promise<void> => {
      this.store.labelStore.wbFiles.data = await this.api.label.wbFiles(wbAccountId)
    }

  loadYandexMarketFiles =
    async (yandexMarketAccountId: number): Promise<void> => {
      this.store.labelStore.yandexMarketFiles.data = await this.api.label.yandexMarketFiles(yandexMarketAccountId)
    }

  setCommissionsSettings =
    async (settings: CommisionsSettings) => {
      await this.api.userSync.setCommissionsSettings(settings)

      setTimeout(this.loadUserAccounts)
    }

  getSetAccountSyncFunc =
    (integrationType: StoreIntegrationType) => {
      switch (integrationType) {
        case IntegrationType.Ozon:
          return this.setOzonAccountSync

        case IntegrationType.YandexMarket:
          return this.setYandexMarketAccountSync

        case IntegrationType.Wildberries:
          return this.setWbAccountSync

        default:
          throw new UnprocessedValueError('IntegrationType', integrationType)
      }
    }

  downloadFilesAsZip = async (fileUrls: string[], zipName: string) => {
    const zip = new JSZip()

    try {
      await Promise.all(
        fileUrls.map(async url => {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Ошибка загрузки файла: ${url}`)
          }
          const contentDisposition = response.headers.get('Content-Disposition')
          let fileName = 'downloaded-file.pdf'

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+?)"/)
            if (match && match[1]) {
              fileName = match[1]
            }
          }

          const blob = await response.blob()
          zip.file(fileName, blob)
        })
      )

      const zipBlob = await zip.generateAsync({type: 'blob'})

      const link = document.createElement('a')
      const zipUrl = URL.createObjectURL(zipBlob)
      link.href = zipUrl
      link.download = zipName + '.zip'
      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(zipUrl)
    } catch (error) {
      console.error('Ошибка при создании ZIP:', error)
      this.showUiError('Ошибка при создании архива!')
    }
  }

  getZipNameAndFiles = (printResult: PrintResult, baseZipName: string) => {
    let zipName = baseZipName

    const fileUrls = [printResult.labelsFileUrl]

    if (printResult.purchaseListFileUrl != null) {
      fileUrls.push(printResult.purchaseListFileUrl)
      zipName += '_with_purchaseList'
    }

    if (printResult.msBarcodesFileUrl != null) {
      fileUrls.push(printResult.msBarcodesFileUrl)
      zipName += '_with_msBarcodes'
    }

    return {
      zipName,
      fileUrls
    }
  }
}